import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from '@mui/material';

import Icon2D from '../../assets/toolbar_icons/Icon2D.svg?react';
import Icon3D from '../../assets/toolbar_icons/Icon3D.svg?react';
import { PLAN_FILE_ACCEPT } from './planFiles';
import { CAD_SCALE_RATIOS, CadScaleRatio, CadTool, CadViewMode } from './types';

interface CadBottomToolbarProps {
  viewMode: CadViewMode;
  scaleSet: boolean;
  metersPerUnit: number;
  scaleRatio: CadScaleRatio | null;
  tool: CadTool;
  onViewModeChange: (mode: CadViewMode) => void;
  onSetScaleTool: () => void;
  onScaleRatioChange: (metersPerUnit: number, scaleRatio: CadScaleRatio | null) => void;
  onImportFile: (file: File) => void;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
}

export function CadBottomToolbar({
  viewMode,
  scaleSet,
  metersPerUnit,
  scaleRatio,
  tool,
  onViewModeChange,
  onSetScaleTool,
  onScaleRatioChange,
  onImportFile,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
}: CadBottomToolbarProps) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [customInput, setCustomInput] = useState('');
  const [showCustom, setShowCustom] = useState(false);

  const btnCls = (active: boolean) =>
    `w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
      active ? 'text-[#d4ff33]' : 'text-white hover:bg-white/10'
    }`;

  /** Find which predefined ratio matches the current metersPerUnit (if any) */
  const currentRatioLabel =
    scaleRatio ??
    (() => {
      const match = CAD_SCALE_RATIOS.find((r) => Math.abs(r.ratio * 0.001 - metersPerUnit) < 1e-9);
      return match?.value ?? 'custom';
    })();

  const handleRatioSelect = (value: string) => {
    if (value === 'custom') {
      setShowCustom(true);
      return;
    }
    const entry = CAD_SCALE_RATIOS.find((r) => r.value === value);
    if (entry) {
      // Convention: 1 coordinate unit on paper = 1mm, so 1:N means 1mm = N mm = N*0.001 m
      onScaleRatioChange(entry.ratio * 0.001, entry.value);
    }
  };

  const handleCustomSubmit = () => {
    const match = customInput.match(/^1\s*:\s*(\d+(?:\.\d+)?)$/);
    if (match) {
      const ratio = parseFloat(match[1]);
      if (ratio > 0) {
        onScaleRatioChange(ratio * 0.001, null);
        setShowCustom(false);
        setCustomInput('');
      }
    }
  };

  return (
    <>
      <input
        type="file"
        accept={PLAN_FILE_ACCEPT}
        ref={fileRef}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onImportFile(f);
          e.target.value = '';
        }}
        className="hidden"
      />

      <div className="absolute bottom-[22px] left-1/2 -translate-x-1/2 flex items-center bg-[#2A2A2A] border border-white/10 rounded-xl p-1 shadow-2xl z-50 gap-0.5">
        {/* Undo / Redo */}
        <Tooltip title={`${t('common.undo', 'Undo')} (⌘Z)`} placement="top">
          <span>
            <button
              onClick={onUndo}
              disabled={!canUndo}
              className={`${btnCls(false)} ${!canUndo ? 'opacity-30 cursor-not-allowed' : ''}`}
            >
              <svg
                className="w-5 h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="1 4 1 10 7 10" />
                <path d="M3.51 15a9 9 0 105.64-11.36L1 10" />
              </svg>
            </button>
          </span>
        </Tooltip>
        <Tooltip title={`${t('common.redo', 'Redo')} (⌘⇧Z)`} placement="top">
          <span>
            <button
              onClick={onRedo}
              disabled={!canRedo}
              className={`${btnCls(false)} ${!canRedo ? 'opacity-30 cursor-not-allowed' : ''}`}
            >
              <svg
                className="w-5 h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="23 4 23 10 17 10" />
                <path d="M20.49 15a9 9 0 11-5.64-11.36L23 10" />
              </svg>
            </button>
          </span>
        </Tooltip>

        {/* Divider */}
        <div className="w-[1px] h-7 bg-white/10 mx-1" />

        {/* Scale Section */}
        {!scaleSet ? (
          /* Before calibration: Set Scale button */
          <Tooltip title={t('projects.cadProject.setScale', 'Set Scale')} placement="top">
            <button
              onClick={onSetScaleTool}
              className={`flex items-center gap-1.5 px-3 h-9 rounded-lg text-xs font-semibold transition-colors ${
                tool === 'set-scale'
                  ? 'bg-[#d4ff33] text-black'
                  : 'bg-red-500/20 text-red-300 hover:bg-red-500/30 border border-red-500/30'
              }`}
            >
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21.3 15.3a2.4 2.4 0 010 3.4l-2.6 2.6a2.4 2.4 0 01-3.4 0L2.7 8.7a2.4 2.4 0 010-3.4l2.6-2.6a2.4 2.4 0 013.4 0z" />
                <line x1="14.5" y1="12.5" x2="11.5" y2="9.5" />
                <line x1="11.5" y1="15.5" x2="8.5" y2="12.5" />
              </svg>
              {t('projects.cadProject.setScale', 'Set Scale')}
            </button>
          </Tooltip>
        ) : (
          /* After calibration: Scale ratio dropdown + recalibrate */
          <div className="flex items-center gap-1 px-1">
            {showCustom ? (
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  value={customInput}
                  onChange={(e) => setCustomInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCustomSubmit();
                    if (e.key === 'Escape') {
                      setShowCustom(false);
                      setCustomInput('');
                    }
                  }}
                  placeholder="1:100"
                  autoFocus
                  className="w-20 h-7 bg-[#0a0e14] border border-white/15 text-white text-[11px] rounded px-2 outline-none focus:border-[#d4ff33]/50 font-mono"
                />
                <button onClick={handleCustomSubmit} className="text-[10px] text-[#d4ff33] hover:text-white px-1">
                  ✓
                </button>
              </div>
            ) : (
              <select
                value={currentRatioLabel}
                onChange={(e) => handleRatioSelect(e.target.value)}
                className="h-8 bg-[#1a1a1a] border border-white/10 text-white text-[11px] rounded px-1 outline-none text-center font-medium"
              >
                {CAD_SCALE_RATIOS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.value}
                  </option>
                ))}
                {currentRatioLabel === 'custom' && <option value="custom">Custom</option>}
                <option value="custom">Custom...</option>
              </select>
            )}

            {/* Recalibrate button */}
            <Tooltip title={t('projects.cadProject.recalibrate', 'Recalibrate scale')} placement="top">
              <button onClick={onSetScaleTool} className={btnCls(tool === 'set-scale')}>
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21.3 15.3a2.4 2.4 0 010 3.4l-2.6 2.6a2.4 2.4 0 01-3.4 0L2.7 8.7a2.4 2.4 0 010-3.4l2.6-2.6a2.4 2.4 0 013.4 0z" />
                  <line x1="14.5" y1="12.5" x2="11.5" y2="9.5" />
                </svg>
              </button>
            </Tooltip>
          </div>
        )}

        {/* Divider */}
        <div className="w-[1px] h-7 bg-white/10 mx-1" />

        {/* 2D / 3D Toggle */}
        <Tooltip title={t('projects.cadProject.toggle2D3D')} placement="top">
          <button
            onClick={() => onViewModeChange(viewMode === '2d' ? '3d' : '2d')}
            className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors text-white hover:bg-white/10`}
          >
            {viewMode === '2d' ? <Icon2D /> : <Icon3D />}
          </button>
        </Tooltip>

        {/* Divider */}
        <div className="w-[1px] h-7 bg-white/10 mx-1" />

        {/* Import File */}
        <Tooltip title={t('projects.cadProject.importFile')} placement="top">
          <button onClick={() => fileRef.current?.click()} className={btnCls(false)}>
            <svg
              className="w-5 h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </button>
        </Tooltip>
      </div>
    </>
  );
}
